import { Container, Heading } from "@chakra-ui/react";
import Head from "next/head";

const Terms = () => {
  return (
    <>
      <Head>
        <title>Songcards Terms</title>
        <link rel="icon" href="/songcards.png" />
        <meta
          name="description"
          content="Our terms for artists and collectors"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta itemProp="image" content="https://songcards.io/sc-pink.png" />
        <link rel="canonical" href={`https://songcards.io/terms`} />
        <link rel="apple-touch-icon" href="/icon.png" />
        <meta property="og:image" content="https://songcards.io/sc-pink.png" />
        <meta property="og:url" content="https://songcards.io/terms" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Songcards Terms" />
        <meta
          property="og:description"
          content="Our terms for artists and collectors"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Songcards Terms" />
        <meta
          name="twitter:description"
          content="Our terms for artists and collectors"
        />
        <meta name="twitter:image" content="https://songcards.io/sc-pink.png" />
      </Head>
      <Container maxW={600} mb={300}>
        <Heading mb={5} mt={5} as="h1">
          Songcards Platform Terms of Service
        </Heading>
        <section>
          <Heading mb={5} mt={5}>
            1. Definitions
          </Heading>
          <ul>
            <li>
              <strong>&quot;Songcards&quot;</strong> or{" "}
              <strong>&quot;the Platform&quot;</strong> refers to the Songcards
              mobile applications (iOS and Android) and web platform operated by
              Songcards Ltd (&quot;we,&quot; &quot;us,&quot; or
              &quot;our&quot;).
            </li>
            <li>
              <strong>&quot;Artist&quot;</strong> refers to any user who uploads
              musical content, artwork, or related materials to the Platform for
              distribution and sale.
            </li>
            <li>
              <strong>&quot;Collector&quot;</strong> refers to end-users who
              purchase content through the Platform.
            </li>
            <li>
              <strong>&quot;Content&quot;</strong> refers to any music, artwork,
              metadata, or other materials uploaded to the Platform.
            </li>
            <li>
              <strong>&quot;Platform Partners&quot;</strong> refers to Apple
              Inc., Google LLC, and Stripe, who process payments on our behalf.
            </li>
          </ul>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            2. Content License and Rights
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              2.1 Artist Authorization
            </Heading>
            <p>By uploading Content to Songcards, Artists explicitly:</p>
            <ul>
              <li>
                Warrant they own or have secured all necessary rights, licenses,
                and permissions to distribute the Content through the Platform
                and all associated mobile applications
              </li>
              <li>
                Grant Songcards and our Platform Partners a non-exclusive,
                worldwide license to store, display, distribute, and sell the
                Content through our mobile and web platforms
              </li>
              <li>
                Confirm they have all required rights to any third-party content
                included in their uploads (including musical compositions,
                samples, artwork, etc.)
              </li>
              <li>
                Authorize Songcards to make the Content available for purchase
                and download by Collectors through all supported platforms and
                payment systems
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              2.2 Representation and Warranties
            </Heading>
            <p>Artists represent and warrant that:</p>
            <ul>
              <li>
                They are the sole owner of the Content or have obtained all
                necessary rights from any and all co-owners, publishers, and
                third parties
              </li>
              <li>
                Their Content does not infringe upon any third party&apos;s
                intellectual property rights
              </li>
              <li>
                They have the full right and authority to enter into this
                agreement
              </li>
              <li>
                All information provided about the Content is accurate and
                complete
              </li>
              <li>
                They have the right to distribute the Content through all
                Platform Partners&apos; services
              </li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            3. Content Distribution
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              3.1 Platform Rights
            </Heading>
            <p>Songcards and its Platform Partners are authorized to:</p>
            <ul>
              <li>
                Display and distribute the Content through our mobile
                applications and web platform
              </li>
              <li>
                Process payments for Content sales through approved payment
                systems
              </li>
              <li>Store and deliver Content to authorized Collectors</li>
              <li>
                Use Content metadata and artwork for promotional purposes within
                the Platform and associated app stores
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              3.2 Collector Rights
            </Heading>
            <p>Collectors who purchase Content receive:</p>
            <ul>
              <li>
                A non-exclusive, non-transferable license for personal use only
              </li>
              <li>
                The right to download and play the Content on personal devices
              </li>
              <li>
                Access to purchased Content through their Songcards account
                across all supported platforms
              </li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            4. Commercial Terms
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              4.1 Revenue Share
            </Heading>
            <p>
              Artists receive 90% of net revenue after applicable platform and
              payment processing fees:
            </p>
            <ul>
              <li>
                <strong>Web Platform (Stripe)</strong>: Revenue calculation
                after Stripe&apos;s processing fees
              </li>
              <li>
                <strong>iOS App (Apple)</strong>: Revenue calculation after
                Apple&apos;s standard commission
              </li>
              <li>
                <strong>Android App (Google Play)</strong>: Revenue calculation
                after Google Play&apos;s standard commission
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              4.2 Payment Processing
            </Heading>
            <p>Each platform uses its required payment system:</p>
            <ul>
              <li>iOS App: Apple&apos;s In-App Purchase system exclusively</li>
              <li>
                Android App: Google Play&apos;s In-App Billing system
                exclusively
              </li>
              <li>Web Platform: Stripe payment processing</li>
              <li>
                Each system&apos;s standard fees and terms apply to respective
                purchases
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              4.3 Platform-Specific Terms
            </Heading>
            <div>
              <h4>Apple In-App Purchases</h4>
              <ul>
                <li>
                  All digital content purchases within the iOS app use
                  Apple&apos;s In-App Purchase system
                </li>
                <li>Apple&apos;s standard commission applies</li>
                <li>
                  Content purchased through Apple is subject to Apple&apos;s
                  standard terms
                </li>
              </ul>
            </div>

            <div>
              <h4>Google Play In-App Billing</h4>
              <ul>
                <li>
                  All digital content purchases within the Android app use
                  Google Play&apos;s In-App Billing
                </li>
                <li>Google Play&apos;s standard commission applies</li>
                <li>
                  Content purchased through Google Play is subject to Google
                  Play&apos;s standard terms
                </li>
              </ul>
            </div>

            <div>
              <h4>Web Platform Purchases</h4>
              <ul>
                <li>Processed through Stripe</li>
                <li>Subject to Stripe&apos;s standard processing fees</li>
                <li>Fees viewable at: https://stripe.com/gb/pricing</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            5. Content Removal and Termination
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              5.1 Artist Withdrawal
            </Heading>
            <ul>
              <li>Artists may request Content removal through our platform</li>
              <li>Upon removal, Content will be delisted from all platforms</li>
              <li>
                Previously purchased Content remains accessible to Collectors
              </li>
              <li>Removal doesn&apos;t affect prior sales or earned revenue</li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              5.2 Platform Rights
            </Heading>
            <p>Songcards reserves the right to:</p>
            <ul>
              <li>
                Remove Content that violates these terms or Platform
                Partners&apos; policies
              </li>
              <li>Suspend or terminate accounts for terms violations</li>
              <li>Modify these terms with notice to users</li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            6. Intellectual Property
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              6.1 Rights Ownership
            </Heading>
            <ul>
              <li>
                Artists retain all ownership rights to their original Content
              </li>
              <li>Songcards claims no ownership over Artist Content</li>
              <li>
                Platform&apos;s branding, design, and features remain property
                of Songcards
              </li>
              <li>
                Platform Partners retain their respective intellectual property
                rights
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              6.2 Prohibited Content
            </Heading>
            <p>Users may not upload:</p>
            <ul>
              <li>Content they don&apos;t have rights to distribute</li>
              <li>
                Content containing unauthorized third-party intellectual
                property
              </li>
              <li>
                Content that violates any applicable laws, regulations, or
                Platform Partners&apos; policies
              </li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            7. Personal Use License
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              7.1 License Limitations
            </Heading>
            <ul>
              <li>
                Content purchases are for personal, non-commercial use only
              </li>
              <li>This is not a commercial licensing platform</li>
              <li>No public performance or commercial rights are granted</li>
              <li>Resale or redistribution of Content is prohibited</li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            8. Cross-Platform Access
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              8.1 Content Availability
            </Heading>
            <ul>
              <li>
                Collectors can access purchased Content across all supported
                platforms
              </li>
              <li>
                Purchase history and content rights are maintained across
                platforms
              </li>
              <li>
                Platform-specific restrictions apply to purchasing methods only
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              8.2 Platform Restrictions
            </Heading>
            <ul>
              <li>
                iOS purchases must occur through Apple&apos;s In-App Purchase
                system
              </li>
              <li>
                Android purchases must occur through Google Play&apos;s In-App
                Billing
              </li>
              <li>Web purchases occur through Stripe</li>
              <li>Content access is not restricted by purchase location</li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            9. Updates and Modifications
          </Heading>
          <ul>
            <li>These terms may be updated periodically</li>
            <li>Changes will be posted on the Platform</li>
            <li>
              Continued use of the Platform constitutes acceptance of modified
              terms
            </li>
            <li>
              Platform Partner terms may change independently of these terms
            </li>
          </ul>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            10. Compliance with Platform Policies
          </Heading>

          <div>
            <Heading mb={5} mt={5}>
              10.1 iOS App Store Compliance
            </Heading>
            <ul>
              <li>
                Songcards iOS app complies with all Apple App Store Guidelines
              </li>
              <li>
                All digital content transactions in iOS use Apple&apos;s In-App
                Purchase system
              </li>
              <li>No alternative payment systems are offered in the iOS app</li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              10.2 Google Play Store Compliance
            </Heading>
            <ul>
              <li>
                Songcards Android app complies with all Google Play Store
                Policies
              </li>
              <li>
                All digital content transactions in Android use Google
                Play&apos;s In-App Billing
              </li>
              <li>
                No alternative payment systems are offered in the Android app
              </li>
            </ul>
          </div>

          <div>
            <Heading mb={5} mt={5}>
              10.3 Web Platform Compliance
            </Heading>
            <ul>
              <li>
                Web platform follows all applicable e-commerce regulations
              </li>
              <li>
                Stripe payment processing complies with relevant financial
                regulations
              </li>
            </ul>
          </div>
        </section>

        <section>
          <Heading mb={5} mt={5}>
            11. Governing Law
          </Heading>
          <p>
            These terms are governed by and construed in accordance with the
            laws of the United Kingdom, subject to applicable Platform Partner
            terms and conditions in their respective jurisdictions.
          </p>
        </section>
      </Container>
    </>
  );
};

export default Terms;
